import Dinero from 'dinero.js';
import Link from 'next/link';
import { memo, useCallback, useMemo, useState } from 'react';

import { useTrackPlanUpgradeConfirmed } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { type PlanInfoType } from '~/components/Modals/PlansModal/utils';
import { Routes } from '~/constants/routes';
import { useBillingCycle } from '~/hooks/useBillingCycle';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useUpdateSubscription } from '~/swr-hooks/subscriptions/useUpdateSubscription';
import { isBillingCycle, isSubscriptionOnTrial } from '~/utils/SubscriptionUtils';

export type PlanChangeConfirmationModalProps = Pick<
  PlanInfoType,
  'planId' | 'planTitle' | 'stripePlanIds' | 'prices' | 'pricingModel'
> & {
  action: Extract<
    PlanInfoType['action'],
    'self-service-downgrade' | 'self-service-upgrade' | 'self-service-upgrade-trial'
  >;
};

export const PlanChangeConfirmationModal = memo(
  ({
    action,
    planId,
    planTitle,
    stripePlanIds,
    prices,
    pricingModel,
    onClose,
  }: AirModalProps<PlanChangeConfirmationModalProps>) => {
    const { showToast } = useToasts();
    const [isLoading, setIsLoading] = useState(false);
    const [_, closePlansModal] = useAirModal(PlansModal);
    const { currentWorkspace } = useCurrentWorkspace();
    const { updateSubscription } = useUpdateSubscription();
    const [billingCycle, setBillingCycle] = useBillingCycle();
    const { data: subscription } = useSubscriptionContext();
    const isOnTrial = isSubscriptionOnTrial(subscription);
    const { trackPlanUpgradeConfirmed } = useTrackPlanUpgradeConfirmed();

    const { title, description, confirmButtonText, cancelButtonText, toastMessage } = useMemo(() => {
      const defaultPlanChangeDescription = `Your next invoice will be automatically prorated to reflect your workspace's new subscription.`;

      switch (action) {
        case 'self-service-downgrade': {
          if (isOnTrial) {
            return {
              title: 'Cancel trial?',
              description: 'Your trial will be cancelled and you will immediately lose access to paid features.',
              confirmButtonText: 'Cancel Trial',
              cancelButtonText: 'Keep Trial',
              toastMessage: 'Your trial has been cancelled',
            };
          } else {
            return {
              title: `Downgrade to ${planTitle}?`,
              description: defaultPlanChangeDescription,
              confirmButtonText: 'Change Plan',
              cancelButtonText: 'Keep Subscription',
              toastMessage: 'Successfully changed your plan',
            };
          }
        }
        case 'self-service-upgrade':
          return {
            title: `Upgrade to ${planTitle}?`,
            description: defaultPlanChangeDescription,
            confirmButtonText: 'Upgrade Plan',
            toastMessage: 'Your purchase was successful',
          };
        case 'self-service-upgrade-trial':
          return {
            title: 'Start your free trial?',
            description: defaultPlanChangeDescription,
            confirmButtonText: 'Start Free Trial',
            toastMessage: 'Your purchase was successful',
          };
      }
    }, [action, isOnTrial, planTitle]);

    const onPlanChange = async () => {
      const workspaceId = currentWorkspace?.id;
      const stripePlanId = stripePlanIds[billingCycle];

      setIsLoading(true);
      try {
        if (!workspaceId) {
          throw new Error('No workspace id');
        }

        if (!stripePlanId) {
          throw new Error('No stripe plan id found for selected plan/billing cycle');
        }

        const isTrial = action === 'self-service-upgrade-trial';
        await updateSubscription({ workspaceId, planId: stripePlanId, trial: isTrial });

        if (action === 'self-service-upgrade' || action === 'self-service-upgrade-trial') {
          trackPlanUpgradeConfirmed({
            old_plan: subscription?.plan ?? 'basic',
            new_plan: planId,
            plan_type: billingCycle,
          });
        }

        closePlansModal();
        onClose();

        if (toastMessage) {
          showToast(toastMessage);
        }
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to update plan via plan change confirmation modal',
          metadata: {
            data: {
              workspaceId,
              planId,
              stripePlanId,
              action,
            },
          },
        });
        showToast('Failed to update plan. Please try again later.');
        setIsLoading(false);
      }
    };

    const formattedPrices = useMemo(() => {
      const CENTS_PER_DOLLAR = 100;

      return {
        monthly: prices.monthly
          ? Dinero({ amount: prices.monthly }).toFormat(prices.monthly % CENTS_PER_DOLLAR ? '$0,0.00' : '$0,0')
          : 'Free',
        annual: prices.annual
          ? Dinero({ amount: prices.annual }).toFormat(prices.annual % CENTS_PER_DOLLAR ? '$0,0.00' : '$0,0')
          : 'Free',
      };
    }, [prices]);

    const onBillingCycleChange = useCallback(
      (value: string) => {
        if (isBillingCycle(value)) {
          setBillingCycle(value);
        }
      },
      [setBillingCycle],
    );

    const priceDetail = pricingModel === 'perMember' ? 'per member per month' : 'per month';

    return (
      <Modal dangerouslyBypassFocusLock data-testid="PLAN_CHANGE_CONFIRMATION_MODAL" isOpen onDismiss={onClose}>
        <header className="mb-4 flex flex-col gap-2">
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
        </header>

        <div className="mb-2 font-semibold text-16 text-gray-12">Select your billing cycle</div>
        <RadioGroup value={billingCycle} onValueChange={onBillingCycleChange}>
          <RadioGroupItem align="center" id="monthly" value="monthly">
            <div className="font-medium text-16 text-gray-12">Monthly</div>
            <div className="font-normal text-14 text-gray-10">
              {formattedPrices.monthly} {priceDetail} billed monthly
            </div>
          </RadioGroupItem>
          <RadioGroupItem align="center" id="annual" value="annual">
            <div className="font-medium text-16 text-gray-12">Annual - Save 17%</div>
            <div className="font-normal text-14 text-gray-10">
              {formattedPrices.annual} {priceDetail} billed annually
            </div>
          </RadioGroupItem>
        </RadioGroup>

        <div className="mt-4 text-14 text-gray-11">
          By confirming your subscription, you allow Air to charge your card for this payment and future payments in
          accordance with their{' '}
          <Link href={Routes.marketing.terms} target="_blank">
            terms
          </Link>
          .
        </div>

        <footer className="mt-8 flex justify-end gap-2">
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            {cancelButtonText || 'Cancel'}
          </Button>
          <Button
            data-testid="PLAN_CHANGE_CONFIRMATION_MODAL_BUTTON"
            appearance="filled"
            color="blue"
            isLoading={isLoading}
            onClick={onPlanChange}
            size="large"
          >
            {confirmButtonText}
          </Button>
        </footer>
      </Modal>
    );
  },
);

PlanChangeConfirmationModal.displayName = 'PlanChangeConfirmationModal';
